import {
  Box,
  IconButton,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
  useTheme,
} from "@mui/material";
import { CloseIcon, ExpandArrowIcon } from "Components/Shared/Icons";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";

type ValueType = string | number;

type Option = {
  value: ValueType;
  label: string;
};

type Props = {
  label: string;
  options: Option[];
  value: ValueType;
  onChange: (value: ValueType) => void;
  placeholder?: string;
  disabled?: boolean;
  accessibilityLabel?: string;
};

const DropdownIcon = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: "absolute",
        right: 12,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        top: "50%",
        transform: "translateY(-50%)",
        "& svg": {
          width: 16,
          height: 16,
          color: theme.palette.text.secondary,
          opacity: 0.7,
        },
      }}
    >
      <ExpandArrowIcon />
    </Box>
  );
};

const renderValue = (
  value: string | number | "",
  options: Option[],
  label: string,
  placeholder?: string,
): ReactNode => {
  const commonStyles = {
    color: "text.primary",
    fontSize: 16,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  if (!value || value === "") {
    return <Typography sx={commonStyles}>{placeholder || label}</Typography>;
  }

  const selectedOption = options.find((opt) => opt.value === value);
  return (
    <Typography sx={{ ...commonStyles, marginRight: "auto" }}>
      {selectedOption ? selectedOption.label : placeholder || label}
    </Typography>
  );
};

export const FilterDropdown = ({
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  accessibilityLabel,
}: Props) => {
  const theme = useTheme();
  const { t } = useResource();

  const handleChange = (event: SelectChangeEvent<ValueType>) => {
    onChange(event.target.value ?? "");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const hasValue = value !== "" && value !== null && value !== undefined;

  return (
    <Box position="relative" display="inline-block" width="100%">
      <Select
        value={value}
        onChange={handleChange}
        disabled={disabled}
        displayEmpty
        variant="outlined"
        IconComponent={DropdownIcon}
        inputProps={{
          "aria-label": accessibilityLabel || label,
        }}
        renderValue={(selected) =>
          renderValue(selected, options, label, placeholder)
        }
        sx={{
          width: "100%",
          backgroundColor: "grey.300",
          borderRadius: "20px",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
          height: 40,
          "&:before, &:after": {
            content: "none",
          },
          "& .MuiSelect-select": {
            padding: `${theme.spacing(1.5)} ${theme.spacing(3)} !important`,
            paddingRight: `${theme.spacing(hasValue ? 11 : 8)} !important`,
            minHeight: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            color: "text.primary",
            fontSize: 16,
            fontWeight: 500,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover": {
            backgroundColor: "grey.400",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
          "&.Mui-focused": {
            backgroundColor: "grey.400",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
          "&.Mui-disabled": {
            backgroundColor: "grey.500",
            opacity: 0.6,
          },
        }}
      >
        {options.length === 0 ? (
          <MenuItem disabled>
            {t(Resources.Components.Shared.Dropdown.NoOptions)}
          </MenuItem>
        ) : (
          options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                "& .MuiTouchRipple-root": {
                  color: "primary.main",
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))
        )}
      </Select>
      {hasValue && <ClearButton onClick={handleClear} />}
    </Box>
  );
};

type ClearButtonProps = {
  onClick: (e: React.MouseEvent) => void;
};

const ClearButton = ({ onClick }: ClearButtonProps) => (
  <IconButton
    onClick={onClick}
    aria-label="Clear filter"
    size="small"
    sx={{
      position: "absolute",
      right: 36,
      top: "50%",
      transform: "translateY(-50%)",
      padding: 0.5,
      zIndex: 1,
      "& svg": {
        width: 14,
        height: 14,
        color: "text.primary",
        opacity: 0.7,
      },
      "&:hover": {
        backgroundColor: "transparent",
        "& svg": {
          opacity: 1,
        },
      },
    }}
  >
    <CloseIcon />
  </IconButton>
);
